import { createSlice } from '@reduxjs/toolkit'

import { ProductReducerState } from '../types'
import extractProductsFromMenuTree from '../utils/extractProductsFromMenuTree'

import productReducers from './productReducers'
import { fetchOutletMenuGroups, fetchProductDetails } from './productThunks'
import productEntityAdapter from './productEntityAdapter'

const productInitialState: ProductReducerState =
  productEntityAdapter.getInitialState({
    selectedProductId: null,
    productDetails: null,

    fetchProductDetailsPending: false,
    menuGroupsFetchPending: false,
    menuGroupsFetchRequestId: null,

    isProductDetailsModalOpen: false,
    isUpsellGroupModalOpen: false,
    isProductSearchModalOpen: false,
  })

const productSlice = createSlice({
  name: 'product',
  initialState: productInitialState,
  reducers: productReducers,
  extraReducers(builder) {
    // Fetch product details
    builder.addCase(fetchProductDetails.pending, state => {
      state.fetchProductDetailsPending = true
    })
    builder.addCase(fetchProductDetails.fulfilled, (state, action) => {
      state.fetchProductDetailsPending = false
      if (state.isProductDetailsModalOpen) {
        if (action.payload) {
          const { productMenuStocks, productDetails } = action.payload
          state.productDetails = productDetails

          if (productMenuStocks && productDetails) {
            const { productId } = productDetails

            productEntityAdapter.updateOne(state, {
              id: productId,
              changes: {
                productStockCounts: productMenuStocks,
              },
            })
          }
        }
      }
    })
    builder.addCase(fetchProductDetails.rejected, state => {
      state.fetchProductDetailsPending = false
    })
    // Fetch menu
    builder.addCase(fetchOutletMenuGroups.pending, (state, action) => {
      state.menuGroupsFetchPending = true
      state.menuGroupsFetchRequestId = action.meta.requestId
      productEntityAdapter.removeAll(state)
    })
    builder.addCase(fetchOutletMenuGroups.fulfilled, (state, action) => {
      /**
       * A superseded menu fetch, e.g. the outlet menu when the company menu
       * override applies, must not overwrite the menu currently in force.
       */
      if (action.meta.requestId !== state.menuGroupsFetchRequestId) {
        return
      }

      state.menuGroupsFetchPending = false
      state.menuGroupsFetchRequestId = null
      productEntityAdapter.setAll(
        state,
        extractProductsFromMenuTree(action.payload),
      )
    })
    builder.addCase(fetchOutletMenuGroups.rejected, (state, action) => {
      if (action.meta.requestId !== state.menuGroupsFetchRequestId) {
        return
      }

      state.menuGroupsFetchPending = false
      state.menuGroupsFetchRequestId = null
    })
  },
})

const productReducer = productSlice.reducer

export const {
  setSelectedProductId,
  setProductUpsellModalVisible,
  setProductDetailsModalVisible,
  setProductSearchModalVisible,
} = productSlice.actions

export default productReducer
